import React, { createContext, useContext, useState, useCallback } from "react";

interface RightSidebarContextType {
  rightSidebarContent: React.ReactNode;
  isRightSidebarOpen: boolean;
  openRightSidebar: (content: React.ReactNode) => void;
  closeRightSidebar: () => void;
}

const RightSidebarContext = createContext<RightSidebarContextType | undefined>(
  undefined
);

export const useRightSidebar = () => {
  const context = useContext(RightSidebarContext);
  if (!context) {
    throw new Error(
      "useRightSidebar must be used within a RightSidebarProvider"
    );
  }
  return context;
};

interface RightSidebarProviderProps {
  children: React.ReactNode;
}

export const RightSidebarProvider: React.FC<RightSidebarProviderProps> = ({
  children,
}) => {
  const [rightSidebarContent, setRightSidebarContent] =
    useState<React.ReactNode>(null);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState<boolean>(false);

  const openRightSidebar = useCallback((content: React.ReactNode) => {
    setRightSidebarContent(content);
    setIsRightSidebarOpen(true);
  }, []);

  const closeRightSidebar = useCallback(() => {
    setRightSidebarContent(null);
    setIsRightSidebarOpen(false);
  }, []);

  const value: RightSidebarContextType = {
    rightSidebarContent,
    isRightSidebarOpen,
    openRightSidebar,
    closeRightSidebar,
  };

  return (
    <RightSidebarContext.Provider value={value}>
      {children}
    </RightSidebarContext.Provider>
  );
};

export default RightSidebarContext;
